import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams, useLocation, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, AlertTriangle, PlayCircle } from "lucide-react";
import TopNav from "@/components/TopNav";
import StepProgressBar from "@/components/StepProgressBar";
import StepPartsButton from "@/components/StepPartsButton";
import { getStepByNo } from "@/data/lampContent";
import { parts } from "@/data/partsList";
import { PartCard } from "@/pages/PartsChecklist";
import {
  getFlow,
  letterOf,
  VOLUNTEERS,
  saveVolunteer,
} from "@/data/volunteerFlows";

const CHECKLIST_KEY = "checklist-checked-parts";

export default function FlowPage() {
  const { volunteer } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const isSteps = pathname.endsWith("/steps");
  const flow = getFlow(volunteer);
  const stepItems = flow
    ? flow.filter((it) => it.type === "step" && it.no !== 8)
    : [];

  // Track checklist completion so the "next" arrow only appears when all parts are ticked.
  const [checkedCount, setCheckedCount] = useState(0);
  useEffect(() => {
    if (isSteps) return undefined;
    const read = () => {
      try {
        const raw = localStorage.getItem(CHECKLIST_KEY);
        const map = raw ? JSON.parse(raw) : {};
        setCheckedCount(parts.filter((p) => map[p.id]).length);
      } catch {
        setCheckedCount(0);
      }
    };
    read();
    window.addEventListener("storage", read);
    const interval = setInterval(read, 400);
    return () => {
      window.removeEventListener("storage", read);
      clearInterval(interval);
    };
  }, [isSteps]);
  const allChecked = checkedCount >= parts.length;

  useEffect(() => {
    if (VOLUNTEERS.includes(volunteer)) saveVolunteer(volunteer);
  }, [volunteer]);

  // If the user came back from /video via the "Back to step N" button,
  // ?goto=step-N tells us where to land. Scroll multiple times so lazy
  // media that loads later doesn't leave us at the wrong offset.
  const gotoId = params.get("goto");
  useEffect(() => {
    if (!gotoId) return undefined;
    const doScroll = () => {
      const el = document.getElementById(gotoId);
      if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
    };
    doScroll();
    const t1 = setTimeout(doScroll, 60);
    const t2 = setTimeout(doScroll, 250);
    const t3 = setTimeout(doScroll, 700);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [gotoId]);

  if (!flow) return <Navigate to="/select-volunteer" replace />;

  return (
    <div
      className="min-h-screen flex flex-col bg-[var(--bg-base)]"
      data-testid="flow-page"
      data-volunteer={volunteer}
      data-mode={isSteps ? "steps" : "checklist"}
    >
      <TopNav />
      {isSteps && <StepProgressBar volunteer={volunteer} />}

      <main
        className={`flex-1 w-full mx-auto max-w-3xl px-4 sm:px-6 pt-6 ${
          isSteps ? "pb-24" : "pb-32"
        } flex flex-col gap-10`}
        data-testid="flow-main"
      >
        {!isSteps && (
          <section
            id="checklist"
            className="scroll-mt-[160px]"
            data-testid="flow-checklist-section"
          >
            <ChecklistSection />
          </section>
        )}

        {isSteps &&
          stepItems.map((item) => (
            <section
              key={item.no}
              id={`step-${item.no}`}
              className="scroll-mt-[160px]"
              data-testid={`flow-step-section-${item.no}`}
            >
              <StepCard item={item} />
            </section>
          ))}

        {isSteps && (
          <div className="pt-2 flex justify-center">
            <button
              type="button"
              onClick={() => navigate("/final-packing")}
              data-testid="flow-testing-btn"
              className="arcade-btn px-6 py-4 text-sm sm:text-base"
            >
              Final Packing
              <ChevronRight strokeWidth={3} className="h-5 w-5" />
            </button>
          </div>
        )}
      </main>

      {!isSteps && (
        <div
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-6"
          data-testid="flow-checklist-bottom-nav"
        >
          <button
            type="button"
            onClick={() => navigate("/select-volunteer")}
            aria-label="Back to role selection"
            data-testid="flow-checklist-prev-btn"
            className="arcade-btn arcade-btn-icon"
          >
            <ChevronLeft strokeWidth={3} className="h-6 w-6" />
          </button>
          <span
            className="flex h-12 min-w-[3.25rem] px-3 items-center justify-center rounded-full border-[3px] border-[var(--border-heavy)] bg-white font-heading text-sm font-bold tabular-nums text-[var(--text-main)] shadow-[3px_3px_0px_var(--border-heavy)]"
            data-testid="flow-checklist-progress-badge"
          >
            {checkedCount}/{parts.length}
          </span>
          {allChecked && (
            <button
              type="button"
              onClick={() => navigate(`/v/${volunteer}/steps`)}
              aria-label="Continue to build steps"
              data-testid="flow-checklist-next-btn"
              className="arcade-btn arcade-btn-icon"
            >
              <ChevronRight strokeWidth={3} className="h-6 w-6" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function ChecklistSection() {
  const [checked, setChecked] = useState(() => {
    try {
      const raw = localStorage.getItem(CHECKLIST_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(CHECKLIST_KEY, JSON.stringify(checked));
    } catch {
      /* ignore */
    }
  }, [checked]);
  const toggle = (id) =>
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.35 }}
      className="card-brut bg-white overflow-hidden"
      data-testid="flow-checklist-card"
    >
      <div className="p-5 sm:p-6 border-b-[3px] border-[var(--border-heavy)]">
        <h2 className="font-heading text-xl sm:text-2xl font-semibold leading-snug text-[var(--text-main)]">
          <span className="text-[#d64533]">Before we begin:</span>{" "}
          <span className="text-[var(--text-main)]">
            Tap the checklist cards to mark off the parts you have.
          </span>
        </h2>
      </div>
      <ul
        className="p-5 sm:p-6 flex flex-col gap-3"
        data-testid="flow-checklist-list"
      >
        {parts.map((p, i) => (
          <PartCard
            key={p.id}
            part={p}
            isChecked={!!checked[p.id]}
            onToggle={() => toggle(p.id)}
            delay={i * 0.02}
          />
        ))}
      </ul>
    </motion.article>
  );
}

function StepCard({ item }) {
  const step = getStepByNo(item.no);
  const videoRef = useRef(null);
  if (!step) return null;

  const substepIndices =
    item.substeps && item.substeps.length > 0
      ? item.substeps
      : (step.substeps || []).map((_, i) => i);

  const badgeLabel = item.label || String(step.no);

  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.35 }}
      className="card-brut bg-white overflow-hidden"
      data-testid={`flow-step-card-${item.no}`}
    >
      <div className="flex items-start gap-4 p-5 sm:p-6 border-b-[3px] border-[var(--border-heavy)]">
        <span
          className="flex h-12 min-w-[3rem] px-2 flex-shrink-0 items-center justify-center rounded-full border-[3px] border-[var(--border-heavy)] bg-[var(--primary)] font-heading text-lg font-bold text-[var(--text-main)] shadow-[3px_3px_0px_var(--border-heavy)]"
          data-testid={`flow-step-badge-${item.no}`}
        >
          {badgeLabel}
        </span>
        <h2
          className="pt-1 font-heading text-xl sm:text-2xl font-semibold leading-snug text-[var(--text-main)]"
          data-testid={`flow-step-title-${item.no}`}
        >
          {step.title}
        </h2>
      </div>

      {step.video && (
        <div
          className="relative w-full aspect-video bg-black border-b-[3px] border-[var(--border-heavy)] overflow-hidden"
          data-testid={`flow-step-media-${item.no}`}
        >
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            className="w-full h-full object-contain"
            data-testid={`flow-step-video-${item.no}`}
          >
            <source src={step.video} type="video/mp4" />
            {step.videoWebm && (
              <source src={step.videoWebm} type="video/webm" />
            )}
          </video>
        </div>
      )}

      {!step.video && step.gif && (
        <div
          className="relative w-full aspect-video bg-white border-b-[3px] border-[var(--border-heavy)] overflow-hidden"
          data-testid={`flow-step-media-${item.no}`}
        >
          <img
            src={step.gif}
            alt="High-five celebration"
            className="w-full h-full object-contain"
            data-testid={`flow-step-gif-${item.no}`}
          />
        </div>
      )}

      {step.substeps && step.substeps.length > 0 && (
        <ol
          className="p-5 sm:p-6 flex flex-col gap-3"
          data-testid={`flow-step-substeps-${item.no}`}
        >
          {substepIndices.map((originalIdx) => (
            <li
              key={originalIdx}
              className="flex items-start gap-3"
              data-testid={`flow-step-${item.no}-sub-${letterOf(originalIdx)}`}
            >
              <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border-2 border-[var(--border-heavy)] bg-white font-heading text-sm font-bold text-[var(--text-main)]">
                {letterOf(originalIdx)}
              </span>
              <p className="text-base sm:text-lg text-[var(--text-main)] leading-relaxed">
                {step.substeps[originalIdx]}
              </p>
            </li>
          ))}
        </ol>
      )}

      {step.note && (
        <div
          className="mx-5 sm:mx-6 mb-5 sm:mb-6 rounded-xl border-2 border-[var(--border-heavy)] bg-[var(--primary)] px-4 py-3 flex items-start gap-3"
          data-testid={`flow-step-note-${item.no}`}
        >
          <AlertTriangle
            strokeWidth={2.5}
            className="h-5 w-5 mt-0.5 flex-shrink-0 text-[var(--text-main)]"
          />
          <p className="text-sm sm:text-base text-[var(--text-main)] leading-relaxed font-body font-semibold [text-wrap:auto]">
            {step.note}
          </p>
        </div>
      )}

      <div className="px-5 sm:px-6 pb-5 sm:pb-6 flex flex-wrap items-center gap-3">
        {!step.gif && (
          <StepPartsButton stepNo={item.no} substeps={item.substeps} />
        )}
        {typeof step.videoStart === "number" && (
          <Link
            to={`/video?t=${step.videoStart}&from=step-${item.no}`}
            data-testid={`flow-step-watch-video-${item.no}`}
            className="arcade-btn px-4 py-3 text-[11px] sm:text-xs"
          >
            <PlayCircle strokeWidth={3} className="h-4 w-4" />
            Watch video
          </Link>
        )}
      </div>
    </motion.article>
  );
}
